import React from "react";
import { useRouterStore, useModulesBackupStore } from "../../../stores";
import {
  Dropdown,
  MenuProps,
  message,
  Avatar,
  Tooltip,
  Badge,
  Modal,
  Button,
  Alert,
} from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getAllModulesByProfileId } from "../../../apis";
import { DialogCloseSession } from "../../../../root/components/dialog";
import "../../css/user-avatar.css";
import {
  triggerSync,
  getPendingSyncCount,
  onSyncCompleted,
  checkForUpdates,
  forceUpdateAndReload,
  checkServerConnection,
} from "../../../helpers/";
import { SyncQueueManager } from "./modal-list-sync";
import IndexedDBManagerModal from "./index-db-manager";
import { ErrorBoundary } from "./error-boundary";

// Constantes
const routeClientUser = "/user/information";
const TOKEN_KEY = process.env.REACT_APP_TOKEN!;
const APP_VERSION = process.env.REACT_APP_VERSION || "v1.0.0";

export const UserAvatar = () => {
  const navigate = useNavigate();
  const firstName = useRouterStore((state) => state.firstName);
  const avatar = useRouterStore((state) => state.avatar);
  const [error, setError] = React.useState<string | null>(null);
  const [openSession, setOpenSession] = React.useState(false);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [pendingItems, setPendingItems] = React.useState(0);
  const [lastSyncTime, setLastSyncTime] = React.useState<Date | null>(null);
  const [updateAvailable, setUpdateAvailable] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [queueModalVisible, setQueueModalVisible] = React.useState(false);
  const [indexedDBManagerVisible, setIndexedDBManagerVisible] =
    React.useState(false);
  const [dropdownVisible, setDropdownVisible] = React.useState(false);

  // Verificar elementos pendientes de sincronización
  React.useEffect(() => {
    const checkPending = async () => {
      try {
        const count = await getPendingSyncCount();
        setPendingItems(count);
      } catch (err) {
        console.error("Error al verificar elementos pendientes:", err);
        setError("Error al verificar elementos pendientes");
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 30000);
    return () => clearInterval(interval);
  }, []);

  // Escuchar eventos de sincronización completada
  React.useEffect(() => {
    const cleanup = onSyncCompleted(({ timestamp }) => {
      setIsSyncing(false);
      setLastSyncTime(timestamp);
      setPendingItems(0);
      message.success("Sincronización completada correctamente");
    });
    return cleanup;
  }, []);

  // Escuchar mensajes de actualización del Service Worker
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "NEW_VERSION_AVAILABLE") {
        setUpdateAvailable(true);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleMessage);
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      }
    };
  }, []);




  const handleSyncData = React.useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);

    try {
      setError(null);

      // ✅ PRIMERO: Verificar conexión con el servidor
      message.loading({
        content: "Verificando conexión con el servidor...",
        key: "sync-check",
        duration: 0,
      });

      const isServerReachable = await checkServerConnection();

      if (!isServerReachable) {
        message.destroy("sync-check");
        message.error({
          content:
            "No se pudo conectar con el servidor. El servidor no respondió en 3 segundos.",
          duration: 5,
        });
        setIsSyncing(false);
        return;
      }

      // ✅ SI HAY CONEXIÓN: Proceder con la sincronización
      message.destroy("sync-check");
      message.loading({
        content: "Sincronizando datos...",
        key: "syncing",
        duration: 0,
      });

      const result = await triggerSync();
      message.destroy("syncing");

      if (!result.success) {
        if (result.noConnection) {
          message.error({
            content: "Error de conectividad: " + result.message,
            duration: 5,
          });
        } else {
          message.warning(result.message);
        }
        setIsSyncing(false);
      } else {
        message.success(result.message);
        // El estado isSyncing se actualizará en onSyncCompleted
      }
    } catch (err: any) {
      message.destroy("sync-check");
      message.destroy("syncing");
      setError(err.message || "Error al sincronizar datos");
      message.error(err.message || "Error al sincronizar datos");
      setIsSyncing(false);
    }
  }, [isSyncing]);

  // Manejar cambios de estado de conexión
  React.useEffect(() => {
    const handleOnline = () => {
      if (pendingItems > 0) {
        message.info(
          "Conexión restablecida. Sincronizando datos pendientes..."
        );
        handleSyncData();
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [pendingItems, handleSyncData]);

  const handleCheckUpdates = React.useCallback(async () => {
    message.loading("Verificando actualizaciones...");
    await checkForUpdates();
    setTimeout(() => {
      if (!updateAvailable) {
        message.success("La aplicación está actualizada");
      }
    }, 2000);
  }, [updateAvailable]);

  const handleUpdate = React.useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    message.loading("Verificando actualizaciones...");
    try {
      setError(null);
      await forceUpdateAndReload();
    } catch (err: any) {
      setError(err.message || "Error al aplicar la actualización");
      message.error(err.message || "Error al aplicar la actualización");
      setIsUpdating(false);
    }
  }, [isUpdating]);

  const handleReloadModules = React.useCallback(async () => {
    try {
      setError(null);
      await reloadModules();
    } catch (error: any) {
      setError(error.message || "Error al recargar módulos");
      message.error(error.message || "Error al recargar módulos");
    }
  }, []);

  const handleOpenQueueManager = React.useCallback(() => {
    if (!navigator.onLine) {
      message.warning(
        "Estás en modo offline. Funcionalidad limitada disponible."
      );
    }
    try {
      setError(null);
      setQueueModalVisible(true);
    } catch (error: any) {
      setError(error.message || "Error al abrir el gestor de cola");
      message.error(error.message || "Error al abrir el gestor de cola");
    }
  }, []);

  const handleOpenIndexedDBManager = React.useCallback(() => {
    // Verificar si IndexedDB está disponible
    if (!window.indexedDB) {
      message.error("IndexedDB no está soportado en este navegador.");
      return;
    }
    try {
      setError(null);
      setIndexedDBManagerVisible(true);
    } catch (error: any) {
      setError(error.message || "Error al abrir el gestor de multimedia");
      message.error(error.message || "Error al abrir el gestor de multimedia");
    }
  }, []);

  const handleQueueModalClose = React.useCallback(() => {
    setQueueModalVisible(false);
    getPendingSyncCount().then((count) => setPendingItems(count));
  }, []);

  const handleCloseSession = React.useCallback(() => {
    setOpenSession(false);
  }, []);

  const formattedLastSync = lastSyncTime
    ? `Última sincronización: ${lastSyncTime.toLocaleTimeString()}`
    : "No se ha sincronizado aún";

  const items: MenuProps["items"] = React.useMemo(
    () => [
      {
        style: {
          backgroundColor: "white",
          color: "#191970",
          margin: "5px",
          borderRadius: "4px",
          overflow: "hidden",
        },
        label: (
          <div
            style={{
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <div style={{ marginRight: "8px" }}>
              <InfoCircleOutlined
                style={{ fontSize: "18px", color: "#191970" }}
              />
            </div>
            <div
              style={{
                maxWidth: "calc(100% - 26px)",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {firstName}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Ver perfil
              </div>
            </div>
          </div>
        ),
        key: "option-one-avatar",
        icon: null,
      },
      {
        style: {
          backgroundColor: "#3CB371",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <p
            style={{
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Recargar Módulos
          </p>
        ),
        key: "option-reload-modules",
        icon: <ReloadOutlined />,
      },
      {
        style: {
          backgroundColor: "#4169E1",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <Tooltip title={formattedLastSync} placement="left">
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                opacity: isSyncing ? 0.7 : 1,
                cursor: isSyncing ? "not-allowed" : "pointer",
              }}
            >
              <span>Sincronizar</span>
              {isSyncing ? (
                <SyncOutlined spin style={{ marginLeft: 5 }} />
              ) : pendingItems > 0 ? (
                <Badge count={pendingItems} size="small" offset={[0, 0]} />
              ) : lastSyncTime ? (
                <CheckCircleOutlined
                  style={{ color: "#52c41a", marginLeft: 5 }}
                />
              ) : null}
            </div>
          </Tooltip>
        ),
        key: "option-sync-data",
        icon: <SyncOutlined spin={isSyncing} />,
      },
      {
        style: {
          backgroundColor: "#8A2BE2",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Gestionar Cola</span>
            {pendingItems > 0 && (
              <Badge count={pendingItems} size="small" offset={[0, 0]} />
            )}
          </div>
        ),
        key: "option-queue-manager",
        icon: <ClockCircleOutlined />,
      },
      {
        style: {
          backgroundColor: "#808000",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <p
            style={{
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Multimedia
          </p>
        ),
        key: "option-indexeddb-manager",
        icon: <DatabaseOutlined />,
      },
      {
        style: {
          backgroundColor: updateAvailable ? "#fa8c16" : "#708090",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              opacity: isUpdating ? 0.7 : 1,
              cursor: isUpdating ? "not-allowed" : "pointer",
            }}
          >
            <span>{updateAvailable ? "Actualizar" : "Check Update"}</span>
            {updateAvailable && !isUpdating && (
              <Badge dot offset={[0, 0]} style={{ marginLeft: 5 }} />
            )}
          </div>
        ),
        key: "option-check-update",
        icon: <ReloadOutlined spin={isUpdating} />,
      },
      {
        style: {
          backgroundColor: "crimson",
          color: "white",
          margin: "5px",
          textAlign: "left",
        },
        label: (
          <p
            style={{
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Cerrar Sesión
          </p>
        ),
        key: "option-three-avatar",
        icon: <CloseCircleOutlined />,
      },
      {
        style: {
          backgroundColor: "#f0f0f0",
          color: "#666",
          margin: "5px",
          textAlign: "center",
          cursor: "default",
        },
        label: (
          <div
            style={{
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            <InfoCircleOutlined /> Versión: {APP_VERSION}
          </div>
        ),
        key: "sw-version-info",
        disabled: true,
      },
    ],
    [
      firstName,
      navigate,
      isSyncing,
      pendingItems,
      lastSyncTime,
      updateAvailable,
      isUpdating,
      formattedLastSync,
    ]
  );

  const handleDropdownClick = React.useCallback(
    ({ key }: { key: string }) => {
      switch (key) {
        case "option-one-avatar":
          navigate(routeClientUser);
          break;
        case "option-reload-modules":
          handleReloadModules();
          break;
        case "option-sync-data":
          if (!isSyncing) handleSyncData();
          break;
        case "option-queue-manager":
          handleOpenQueueManager();
          break;
        case "option-indexeddb-manager":
          handleOpenIndexedDBManager();
          break;
        case "option-check-update":
          if (updateAvailable) handleUpdate();
          else handleCheckUpdates();
          break;
        case "option-three-avatar":
          setOpenSession(true);
          break;
      }
      setDropdownVisible(false);
    },
    [
      navigate,
      handleReloadModules,
      handleSyncData,
      handleOpenQueueManager,
      handleOpenIndexedDBManager,
      handleUpdate,
      handleCheckUpdates,
      isSyncing,
      updateAvailable,
    ]
  );

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <DialogCloseSession open={openSession} handleClose={handleCloseSession} />
      {queueModalVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de cola"
              open={true}
              onCancel={() => setQueueModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setQueueModalVisible(false)}>
                  Cerrar
                </Button>,
              ]}
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de cola."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <SyncQueueManager
            visible={queueModalVisible}
            onClose={handleQueueModalClose}
            onSync={handleSyncData}
          />
        </ErrorBoundary>
      )}
      {indexedDBManagerVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de multimedia"
              open={true}
              onCancel={() => setIndexedDBManagerVisible(false)}
              footer={[
                <Button
                  key="close"
                  onClick={() => setIndexedDBManagerVisible(false)}
                >
                  Cerrar
                </Button>,
              ]}
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de multimedia."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <IndexedDBManagerModal
            visible={indexedDBManagerVisible}
            onClose={() => setIndexedDBManagerVisible(false)}
          />
        </ErrorBoundary>
      )}
      <Dropdown
        destroyPopupOnHide
        menu={{
          items,
          onClick: handleDropdownClick,
        }}
        trigger={["click"]}
        overlayStyle={{ width: 190, margin: "5px" }}
        open={dropdownVisible}
        onOpenChange={setDropdownVisible}
      >
        <Badge
          dot={pendingItems > 0 || updateAvailable}
          offset={[-5, 5]}
          color={
            updateAvailable ? "#fa8c16" : pendingItems > 0 ? "red" : undefined
          }
        >
          <Avatar className="avatar-element" size="large" gap={4} src={avatar}>
            {firstName?.toString().charAt(0) ?? ""}
          </Avatar>
        </Badge>
      </Dropdown>
    </div>
  );
};

export const reloadModules = async () => {
  const { setModules } = useRouterStore.getState();
  const { modulesBackup, setModulesBackup } = useModulesBackupStore.getState();

  if (navigator.onLine) {
    try {
      const tokenData = JSON.parse(localStorage.getItem(TOKEN_KEY) || "{}");
      const token = tokenData.token;
      const profileId = tokenData.user?.profileId;

      if (!token || !profileId) {
        throw new Error("No se encontró un token o profileId");
      }

      const { data: dataModules } = await getAllModulesByProfileId(profileId);
      if (dataModules) {
        setModules(dataModules);
        setModulesBackup(dataModules);
        message.success("Módulos recargados exitosamente");
        return dataModules;
      } else {
        throw new Error("No se encontraron módulos");
      }
    } catch (error: any) {
      if (!navigator.onLine || error.response?.status === 401) {
        if (modulesBackup.length > 0) {
          setModules(modulesBackup);
          message.warning("No hay conexión. Módulos cargados desde el backup.");
          return modulesBackup;
        }
        message.error("No hay conexión y no hay backup disponible.");
        throw new Error("No hay módulos disponibles");
      }
      message.error("Error al recargar módulos.");
      throw error;
    }
  } else {
    if (modulesBackup.length > 0) {
      setModules(modulesBackup);
      message.warning("No hay conexión. Módulos cargados desde el backup.");
      return modulesBackup;
    }
    message.error("No hay conexión y no hay backup disponible.");
    throw new Error("No hay módulos disponibles");
  }
};
